import React from 'react';
import {
  Alert,
  Box,
} from '@mui/material';
import { CollectionFormBuilder } from '../../../base/CollectionFormBuilder';
import { BaseButton, BaseModal } from '../../../base';

export interface EditingCollectionItem {
  id?: string;
  schemaKey: string;
  data: Record<string, any>;
  schema: Record<string, any>;
}

export interface CollectionItemModalProps {
  open: boolean;
  isCreating: boolean;
  editingItem: EditingCollectionItem | null;
  saving: boolean;
  
  // Event handlers
  onClose: () => void;
  onSave: (formData: Record<string, any>) => void;
}

/**
 * Modal for creating/editing collection items
 * Uses CollectionFormBuilder for dynamic form rendering
 */
export const CollectionItemModal: React.FC<CollectionItemModalProps> = ({
  open,
  isCreating,
  editingItem,
  saving,
  onClose,
  onSave,
}) => {
  const formRef = React.useRef<HTMLFormElement>(null);

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(new Event('submit', { bubbles: true }));
    }
  };

  const title = isCreating
    ? `Create New Collection Item: ${editingItem?.schemaKey}`
    : `Edit Collection Item: ${editingItem?.schemaKey}`;

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="md"
      showFooter={true}
      isLoading={saving}
      actions={
        <>
          <BaseButton onClick={onClose} variant="outlined" colorVariant="secondary" disabled={saving}>
            Cancel
          </BaseButton>
          <BaseButton
            onClick={handleSubmit}
            variant="contained"
            colorVariant="primary"
            disabled={saving}
            isLoading={saving}
            loadingLabel="Saving..."
          >
            Save Item
          </BaseButton>
        </>
      }
    >
      {editingItem?.schema && Object.keys(editingItem.schema).length > 0 ? (
        <Box key={`${editingItem?.id || 'create'}`}>
          <CollectionFormBuilder
            ref={formRef}
            schema={editingItem.schema}
            initialData={editingItem.data}
            onSubmit={onSave}
            loading={saving}
            hideSubmitButton={true}
          />
        </Box>
      ) : (
        <Alert severity="info">
          No schema defined for this collection. Please check the collection schema.
        </Alert>
      )}
    </BaseModal>
  );
};

export default CollectionItemModal;
